import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import classes from './App.module.css';
import Seo from './Seo';
import { faqs } from './content';

const TermsAndServices = lazy(() => import('./TermsAndServices'));
const PrivacyPolicy = lazy(() => import('./PrivacyPolicy'));

const APP_STORE_URL = 'https://apps.apple.com/us/app/amevo-relationship-counter/id6759841754';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.portalstudio.sonsuz_ask';

const features = [
  {
    title: 'Live relationship counter',
    text: 'See exactly how long you have been together in years, months, days, hours and minutes.',
  },
  {
    title: 'Milestones in one place',
    text: 'Keep the dates that shaped your story, from the day you met to engagements, weddings and anniversaries.',
  },
  {
    title: 'Meaningful statistics',
    text: 'Discover birthdays, Valentine’s Days and New Years shared, plus progress toward your next big milestone.',
  },
  {
    title: 'Special-day countdowns',
    text: 'Count down to anniversaries, birthdays and every custom date that matters to the two of you.',
  },
  {
    title: 'Thoughtful reminders',
    text: 'Schedule local notifications ahead of important dates, so there is always time to plan something special.',
  },
  {
    title: 'Beautiful sharing cards',
    text: 'Turn your relationship stats into visual cards you can save or share with the people you choose.',
  },
];

function StoreButtons() {
  return (
    <div className={classes.buttonContainer} aria-label="Download Amevo">
      <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className={`${classes.storeBtn} ${classes.appleBtn}`} aria-label="Download Amevo on the Apple App Store">
        <div className={classes.btnIcon} aria-hidden="true">
          <svg viewBox="0 0 384 512" fill="currentColor">
            <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
          </svg>
        </div>
        <div className={classes.btnTexts}>
          <span className={classes.btnSub}>Download on the</span>
          <span className={classes.btnMain}>App Store</span>
        </div>
      </a>
      <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className={`${classes.storeBtn} ${classes.googleBtn}`} aria-label="Get Amevo on Google Play">
        <div className={classes.btnIcon} aria-hidden="true">
          <svg viewBox="0 0 512 512" fill="currentColor">
            <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
          </svg>
        </div>
        <div className={classes.btnTexts}>
          <span className={classes.btnSub}>Get it on</span>
          <span className={classes.btnMain}>Google Play</span>
        </div>
      </a>
    </div>
  );
}

function Header() {
  return (
    <header className={classes.header}>
      <div className={classes.navbar}>
        <Link to="/" className={classes.brand} aria-label="Amevo home">
          <img src="/favicon.svg" width="42" height="42" alt="" />
          <span>Amevo</span>
        </Link>
        <nav className={classes.navLinks} aria-label="Main navigation">
          <Link to="/#features">Features</Link>
          <Link to="/#privacy">Privacy</Link>
          <Link to="/#faq">FAQ</Link>
          <a href="mailto:portalstudio.info@gmail.com">Contact</a>
        </nav>
      </div>
    </header>
  );
}

function Home() {
  return (
    <main>
      <section className={classes.hero}>
        <div className={classes.heroGlow} aria-hidden="true" />
        <div className={classes.logoBox}>
          <img src="/favicon.svg" width="132" height="132" alt="Amevo relationship counter app icon" className={classes.logoImg} fetchPriority="high" />
        </div>
        <p className={classes.eyebrow}>Relationship tracker for iOS &amp; Android</p>
        <h1 className={classes.title}>Your relationship, <span className={classes.gradientText}>beautifully counted.</span></h1>
        <p className={classes.subtitle}>Amevo is a relationship counter and anniversary tracker that turns your time together into meaningful milestones, reminders and shareable memories.</p>
        <StoreButtons />
        <p className={classes.heroNote}>Free to download · No account required · Your content stays on your device</p>
      </section>

      <section id="features" className={classes.section} aria-labelledby="features-title">
        <div className={classes.sectionHeading}>
          <p className={classes.kicker}>Made for your story</p>
          <h2 id="features-title">More than an anniversary counter</h2>
          <p>Keep the moments that matter close, understand the story behind the dates and look forward to what comes next.</p>
        </div>
        <div className={classes.featureGrid}>
          {features.map((feature, index) => (
            <article className={classes.featureCard} key={feature.title}>
              <span className={classes.featureNumber} aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
              <h3>{feature.title}</h3>
              <p>{feature.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="privacy" className={`${classes.section} ${classes.privacySection}`} aria-labelledby="privacy-title">
        <div className={classes.privacyIcon} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="m9 12 2 2 4-4" />
          </svg>
        </div>
        <div className={classes.privacyCopy}>
          <p className={classes.kicker}>Private by design</p>
          <h2 id="privacy-title">Your relationship belongs to you</h2>
          <p>Personal details you add to Amevo are stored locally on your device. There is no Amevo account to create, no email or password to hand over, and no server-side copy of your relationship content.</p>
          <Link to="/privacy-policy" className={classes.textLink}>Read our Privacy Policy <span aria-hidden="true">→</span></Link>
        </div>
      </section>

      <section className={classes.section} aria-labelledby="steps-title">
        <div className={classes.sectionHeading}>
          <p className={classes.kicker}>Simple from day one</p>
          <h2 id="steps-title">Start celebrating in three steps</h2>
        </div>
        <ol className={classes.steps}>
          <li><span>1</span><div><h3>Add your story</h3><p>Enter your relationship start date and the milestones you want to remember.</p></div></li>
          <li><span>2</span><div><h3>Watch time grow</h3><p>Explore live counters, upcoming dates and statistics created from your shared timeline.</p></div></li>
          <li><span>3</span><div><h3>Remember and share</h3><p>Set local reminders and create beautiful cards for the moments worth celebrating.</p></div></li>
        </ol>
      </section>

      <section id="faq" className={`${classes.section} ${classes.faqSection}`} aria-labelledby="faq-title">
        <div className={classes.sectionHeading}>
          <p className={classes.kicker}>Questions, answered</p>
          <h2 id="faq-title">Amevo FAQ</h2>
        </div>
        <div className={classes.faqList}>
          {faqs.map((faq) => (
            <details key={faq.question} className={classes.faqItem}>
              <summary>{faq.question}</summary>
              <p>{faq.answer}</p>
            </details>
          ))}
        </div>
      </section>

      <section className={`${classes.section} ${classes.ctaSection}`} aria-labelledby="cta-title">
        <p className={classes.kicker}>Every moment counts</p>
        <h2 id="cta-title">Keep your story close with Amevo</h2>
        <p>Download the relationship counter made for anniversaries, milestones and all the meaningful days in between.</p>
        <StoreButtons />
      </section>
    </main>
  );
}

function NotFound() {
  return (
    <main className={classes.notFound}>
      <p className={classes.kicker}>404</p>
      <h1>Page not found</h1>
      <p>The page you are looking for does not exist or may have moved.</p>
      <Link to="/" className={classes.textLink}>Return to Amevo</Link>
    </main>
  );
}

function RouteEffects() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      requestAnimationFrame(() => document.getElementById(hash.slice(1))?.scrollIntoView());
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
}

function AppContent() {
  return (
    <div className={classes.app}>
      <Seo />
      <RouteEffects />
      <Header />
      <Suspense fallback={<main className={classes.routeLoading} aria-live="polite">Loading…</main>}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/terms-and-services" element={<TermsAndServices />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <footer className={classes.footer}>
        <div>
          <Link to="/" className={classes.footerBrand}>Amevo</Link>
          <p>A relationship counter for the moments worth remembering.</p>
        </div>
        <nav aria-label="Legal navigation">
          <Link to="/terms-and-services">Terms &amp; Conditions</Link>
          <Link to="/privacy-policy">Privacy Policy</Link>
          <a href="mailto:portalstudio.info@gmail.com">Contact</a>
        </nav>
        <p className={classes.copyright}>&copy; {new Date().getFullYear()} Amevo. All rights reserved.</p>
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
